/**
 * Markets a site can be made for (ADR-0047): a country and the language its
 * readers speak, with what follows from the pair — WordPress's locale, the
 * clock and the way dates are written there. Choosing a market is the only
 * thing a new GEO asks about its audience; everything below comes with it.
 *
 * `language` is the BCP 47 tag hreflang and the SEO checks use (ADR-0045),
 * `country` its ISO 3166 code. `wpLocale` is a locale WordPress ships
 * translations for. `startOfWeek`: 0 Sunday, 1 Monday, 6 Saturday.
 */
export interface Market {
  readonly id: string;
  readonly country: string;
  readonly language: string;
  readonly wpLocale: string;
  readonly timezone: string;
  readonly dateFormat: string;
  readonly timeFormat: string;
  readonly startOfWeek: 0 | 1 | 6;
  /** As the market list shows it: country, then language. */
  readonly label: string;
}

const market = (
  id: string,
  country: string,
  language: string,
  wpLocale: string,
  timezone: string,
  dateFormat: string,
  timeFormat: string,
  startOfWeek: 0 | 1 | 6,
  label: string,
): Market => ({ id, country, language, wpLocale, timezone, dateFormat, timeFormat, startOfWeek, label });

export const MARKETS: readonly Market[] = [
  market('us', 'US', 'en', 'en_US', 'America/New_York', 'F j, Y', 'g:i a', 0, 'США · английский'),
  market('gb', 'GB', 'en-GB', 'en_GB', 'Europe/London', 'j F Y', 'H:i', 1, 'Великобритания · английский'),
  market('ca-en', 'CA', 'en-CA', 'en_CA', 'America/Toronto', 'F j, Y', 'g:i a', 0, 'Канада · английский'),
  market('ca-fr', 'CA', 'fr-CA', 'fr_CA', 'America/Toronto', 'j F Y', 'H:i', 0, 'Канада · французский'),
  market('au', 'AU', 'en-AU', 'en_AU', 'Australia/Sydney', 'j F Y', 'g:i a', 1, 'Австралия · английский'),
  market('nz', 'NZ', 'en-NZ', 'en_NZ', 'Pacific/Auckland', 'j F Y', 'g:i a', 1, 'Новая Зеландия · английский'),
  market('ie', 'IE', 'en-IE', 'en_GB', 'Europe/Dublin', 'j F Y', 'H:i', 1, 'Ирландия · английский'),
  market('in', 'IN', 'en-IN', 'en_GB', 'Asia/Kolkata', 'j F Y', 'g:i a', 0, 'Индия · английский'),
  market('za', 'ZA', 'en-ZA', 'en_ZA', 'Africa/Johannesburg', 'j F Y', 'H:i', 0, 'ЮАР · английский'),
  market('de', 'DE', 'de', 'de_DE', 'Europe/Berlin', 'j. F Y', 'H:i', 1, 'Германия · немецкий'),
  market('at', 'AT', 'de-AT', 'de_AT', 'Europe/Vienna', 'j. F Y', 'H:i', 1, 'Австрия · немецкий'),
  market('ch-de', 'CH', 'de-CH', 'de_CH', 'Europe/Zurich', 'j. F Y', 'H:i', 1, 'Швейцария · немецкий'),
  market('ch-fr', 'CH', 'fr-CH', 'fr_FR', 'Europe/Zurich', 'j F Y', 'H:i', 1, 'Швейцария · французский'),
  market('fr', 'FR', 'fr', 'fr_FR', 'Europe/Paris', 'j F Y', 'H:i', 1, 'Франция · французский'),
  market('be-fr', 'BE', 'fr-BE', 'fr_BE', 'Europe/Brussels', 'j F Y', 'H:i', 1, 'Бельгия · французский'),
  market('be-nl', 'BE', 'nl-BE', 'nl_BE', 'Europe/Brussels', 'j F Y', 'H:i', 1, 'Бельгия · нидерландский'),
  market('nl', 'NL', 'nl', 'nl_NL', 'Europe/Amsterdam', 'j F Y', 'H:i', 1, 'Нидерланды · нидерландский'),
  market('es', 'ES', 'es', 'es_ES', 'Europe/Madrid', 'j \\d\\e F \\d\\e Y', 'H:i', 1, 'Испания · испанский'),
  market('mx', 'MX', 'es-MX', 'es_MX', 'America/Mexico_City', 'j \\d\\e F \\d\\e Y', 'H:i', 0, 'Мексика · испанский'),
  market('ar', 'AR', 'es-AR', 'es_AR', 'America/Argentina/Buenos_Aires', 'j \\d\\e F \\d\\e Y', 'H:i', 1, 'Аргентина · испанский'),
  market('co', 'CO', 'es-CO', 'es_CO', 'America/Bogota', 'j \\d\\e F \\d\\e Y', 'H:i', 0, 'Колумбия · испанский'),
  market('cl', 'CL', 'es-CL', 'es_CL', 'America/Santiago', 'j \\d\\e F \\d\\e Y', 'H:i', 1, 'Чили · испанский'),
  market('br', 'BR', 'pt-BR', 'pt_BR', 'America/Sao_Paulo', 'j \\d\\e F \\d\\e Y', 'H:i', 0, 'Бразилия · португальский'),
  market('pt', 'PT', 'pt-PT', 'pt_PT', 'Europe/Lisbon', 'j \\d\\e F \\d\\e Y', 'H:i', 1, 'Португалия · португальский'),
  market('it', 'IT', 'it', 'it_IT', 'Europe/Rome', 'j F Y', 'H:i', 1, 'Италия · итальянский'),
  market('pl', 'PL', 'pl', 'pl_PL', 'Europe/Warsaw', 'j F Y', 'H:i', 1, 'Польша · польский'),
  market('cz', 'CZ', 'cs', 'cs_CZ', 'Europe/Prague', 'j. n. Y', 'H:i', 1, 'Чехия · чешский'),
  market('sk', 'SK', 'sk', 'sk_SK', 'Europe/Bratislava', 'j. n. Y', 'H:i', 1, 'Словакия · словацкий'),
  market('hu', 'HU', 'hu', 'hu_HU', 'Europe/Budapest', 'Y. F j.', 'H:i', 1, 'Венгрия · венгерский'),
  market('ro', 'RO', 'ro', 'ro_RO', 'Europe/Bucharest', 'j F Y', 'H:i', 1, 'Румыния · румынский'),
  market('bg', 'BG', 'bg', 'bg_BG', 'Europe/Sofia', 'j F Y', 'H:i', 1, 'Болгария · болгарский'),
  market('gr', 'GR', 'el', 'el', 'Europe/Athens', 'j F Y', 'H:i', 1, 'Греция · греческий'),
  market('se', 'SE', 'sv', 'sv_SE', 'Europe/Stockholm', 'j F Y', 'H:i', 1, 'Швеция · шведский'),
  market('dk', 'DK', 'da', 'da_DK', 'Europe/Copenhagen', 'j. F Y', 'H:i', 1, 'Дания · датский'),
  market('no', 'NO', 'nb', 'nb_NO', 'Europe/Oslo', 'j. F Y', 'H:i', 1, 'Норвегия · норвежский'),
  market('fi', 'FI', 'fi', 'fi', 'Europe/Helsinki', 'j.n.Y', 'H:i', 1, 'Финляндия · финский'),
  market('tr', 'TR', 'tr', 'tr_TR', 'Europe/Istanbul', 'j F Y', 'H:i', 1, 'Турция · турецкий'),
  market('ua', 'UA', 'uk', 'uk', 'Europe/Kyiv', 'd.m.Y', 'H:i', 1, 'Украина · украинский'),
  market('ru', 'RU', 'ru', 'ru_RU', 'Europe/Moscow', 'd.m.Y', 'H:i', 1, 'Россия · русский'),
  market('kz', 'KZ', 'ru-KZ', 'ru_RU', 'Asia/Almaty', 'd.m.Y', 'H:i', 1, 'Казахстан · русский'),
  market('jp', 'JP', 'ja', 'ja', 'Asia/Tokyo', 'Y年n月j日', 'H:i', 0, 'Япония · японский'),
  market('kr', 'KR', 'ko', 'ko_KR', 'Asia/Seoul', 'Y년 n월 j일', 'H:i', 0, 'Южная Корея · корейский'),
  market('cn', 'CN', 'zh-CN', 'zh_CN', 'Asia/Shanghai', 'Y年n月j日', 'H:i', 1, 'Китай · китайский'),
  market('tw', 'TW', 'zh-TW', 'zh_TW', 'Asia/Taipei', 'Y年n月j日', 'H:i', 0, 'Тайвань · китайский'),
  market('hk', 'HK', 'zh-HK', 'zh_HK', 'Asia/Hong_Kong', 'Y年n月j日', 'H:i', 0, 'Гонконг · китайский'),
  market('id', 'ID', 'id', 'id_ID', 'Asia/Jakarta', 'j F Y', 'H:i', 1, 'Индонезия · индонезийский'),
  market('my', 'MY', 'ms', 'ms_MY', 'Asia/Kuala_Lumpur', 'j F Y', 'g:i a', 1, 'Малайзия · малайский'),
  market('th', 'TH', 'th', 'th', 'Asia/Bangkok', 'j F Y', 'H:i', 0, 'Таиланд · тайский'),
  market('vn', 'VN', 'vi', 'vi', 'Asia/Ho_Chi_Minh', 'd/m/Y', 'H:i', 1, 'Вьетнам · вьетнамский'),
  market('ph', 'PH', 'en-PH', 'en_US', 'Asia/Manila', 'F j, Y', 'g:i a', 0, 'Филиппины · английский'),
  market('sg', 'SG', 'en-SG', 'en_GB', 'Asia/Singapore', 'j F Y', 'g:i a', 1, 'Сингапур · английский'),
  market('ae', 'AE', 'ar-AE', 'ar', 'Asia/Dubai', 'j F، Y', 'g:i a', 6, 'ОАЭ · арабский'),
  market('sa', 'SA', 'ar-SA', 'ar', 'Asia/Riyadh', 'j F، Y', 'g:i a', 6, 'Саудовская Аравия · арабский'),
  market('il', 'IL', 'he', 'he_IL', 'Asia/Jerusalem', 'j בF Y', 'H:i', 0, 'Израиль · иврит'),
];

const BY_ID = new Map(MARKETS.map((item) => [item.id, item]));

export function findMarket(id: string): Market | null {
  return BY_ID.get(id) ?? null;
}

/** The market a site's country and language name, if there is one. */
export function marketOf(country: string | null, language: string | null): Market | null {
  if (!country || !language) return null;
  return MARKETS.find((item) => item.country === country && item.language === language) ?? null;
}

/**
 * A country as its flag (ADR-0060): the two regional indicator letters every
 * emoji font draws as the flag, «DE» → 🇩🇪. Null for anything that is not a
 * two-letter code.
 */
export function flagOf(country: string | null | undefined): string | null {
  const code = country?.trim().toUpperCase() ?? '';
  if (!/^[A-Z]{2}$/.test(code)) return null;
  return String.fromCodePoint(0x1f1e6 + code.charCodeAt(0) - 65, 0x1f1e6 + code.charCodeAt(1) - 65);
}

/**
 * Where a language is first of all spoken, for a tag that names no country:
 * English is shown with the US flag, Japanese with Japan's. A tag that has a
 * region (pt-BR, en-GB) is shown with that region's flag instead.
 */
const LANGUAGE_HOME: Readonly<Record<string, string>> = {
  ar: 'SA', az: 'AZ', be: 'BY', bg: 'BG', bn: 'BD', cs: 'CZ', da: 'DK', de: 'DE', el: 'GR', en: 'US', es: 'ES', et: 'EE',
  fa: 'IR', fi: 'FI', fil: 'PH', fr: 'FR', he: 'IL', hi: 'IN', hr: 'HR', hu: 'HU', hy: 'AM', id: 'ID', it: 'IT', ja: 'JP',
  ka: 'GE', kk: 'KZ', ko: 'KR', lt: 'LT', lv: 'LV', ms: 'MY', nb: 'NO', nl: 'NL', nn: 'NO', no: 'NO', pl: 'PL', pt: 'PT',
  ro: 'RO', ru: 'RU', sk: 'SK', sl: 'SI', sr: 'RS', sv: 'SE', th: 'TH', tl: 'PH', tr: 'TR', uk: 'UA', ur: 'PK', vi: 'VN',
  zh: 'CN',
};

/** The country a language tag is shown with: its region if it has one, else where the language is at home. */
export function languageCountry(language: string | null | undefined): string | null {
  const parts = language?.trim().split(/[-_]/) ?? [];
  const primary = parts[0]?.toLowerCase() ?? '';
  if (!/^[a-z]{2,3}$/.test(primary)) return null;
  const region = parts.slice(1).find((part) => /^[A-Za-z]{2}$/.test(part));
  if (region) return region.toUpperCase();
  // Chinese written in traditional characters is Taiwan's before it is China's.
  if (primary === 'zh' && parts.some((part) => part.toLowerCase() === 'hant')) return 'TW';
  return LANGUAGE_HOME[primary] ?? null;
}

/** A language as a flag: «ko» → 🇰🇷, «pt-BR» → 🇧🇷. Null when the tag names no language the panel can place. */
export function languageFlag(language: string | null | undefined): string | null {
  return flagOf(languageCountry(language));
}
